import { useMemo } from 'react';

import { Status, type Vulnerability } from '@rtkwlf/mr-infra-risk';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { LinkCell } from '@packages/components/column-store/cell/link-cell/link-cell';
import { booleanColumnType } from '@packages/components/column-store/column/boolean-column-type';
import { datetimeColumnType } from '@packages/components/column-store/column/datetime-column-type';
import { hiddenColumnType } from '@packages/components/column-store/column/hidden-column-type';
import { longStringColumnType } from '@packages/components/column-store/column/long-string-column-type';
import { riskScoreColumnType } from '@packages/components/column-store/column/risk-score-column-type';
import { riskSeverityColumnType } from '@packages/components/column-store/column/risk-severity-column-type';
import { riskSourceColumnType } from '@packages/components/column-store/column/risk-source-column-type';
import { smallStringColumnType } from '@packages/components/column-store/column/small-string-column-type';
import {
  type GridColDef,
  getFilteredGridStringOperators,
  GridStringOperator,
  CustomChip,
} from '@packages/fenrir/components';

import { CVECell } from '../../../components/cve-cell/cve-cell';
import { riskSourceOptions } from '../../../filter-management/select-options/select-options';
import { ALL_RISKS_COLUMN_DEFS } from '../../all-risks/all-risks-table.constants';
import { VULNERABILITY_COLUMN_DEFS } from '../vuln-table.constants';

export const useVulnerabilitiesTableColumns = () => {
  const { t: tRisks } = useTranslation('riskManagement', {
    keyPrefix: 'risks',
  });

  return useMemo<GridColDef<Vulnerability>[]>(
    () => [
      {
        ...longStringColumnType,
        field: VULNERABILITY_COLUMN_DEFS.NAME,
        headerName: tRisks(`tableHeaders.${VULNERABILITY_COLUMN_DEFS.NAME}`),
        sortable: false,
        filterable: false,
      },
      {
        ...smallStringColumnType,
        field: VULNERABILITY_COLUMN_DEFS.RISKS_OPEN,
        headerName: tRisks(
          `tableHeaders.${VULNERABILITY_COLUMN_DEFS.RISKS_OPEN}`,
        ),
        renderCell: (params) => {
          return (
            <LinkCell
              {...params}
              to={generatePath(
                `/risk-management/risks/all?${VULNERABILITY_COLUMN_DEFS.ISSUE_ID}=${params.row.issueID}&sort=-${ALL_RISKS_COLUMN_DEFS.SCORE}&${ALL_RISKS_COLUMN_DEFS.STATUS}=${Status.Unresolved}`,
              )}
            />
          );
        },
        filterable: false,
      },
      {
        ...smallStringColumnType,
        field: VULNERABILITY_COLUMN_DEFS.RISKS_RESOLVED,
        headerName: tRisks(
          `tableHeaders.${VULNERABILITY_COLUMN_DEFS.RISKS_RESOLVED}`,
        ),
        renderCell: (params) => {
          return (
            <LinkCell
              {...params}
              to={generatePath(
                `/risk-management/risks/all?${VULNERABILITY_COLUMN_DEFS.ISSUE_ID}=${params.row.issueID}&sort=-${ALL_RISKS_COLUMN_DEFS.SCORE}&${ALL_RISKS_COLUMN_DEFS.STATUS}=${Status.Resolved}`,
              )}
            />
          );
        },
        filterable: false,
      },
      {
        ...booleanColumnType,
        field: VULNERABILITY_COLUMN_DEFS.CISA_KEV,
        headerName: tRisks(
          `tableHeaders.${VULNERABILITY_COLUMN_DEFS.CISA_KEV}`,
        ),
      },
      {
        ...riskSourceColumnType,
        valueGetter: (_value, row: Vulnerability) => row.sources,
        field: VULNERABILITY_COLUMN_DEFS.SOURCE,
        headerName: tRisks(`tableHeaders.${VULNERABILITY_COLUMN_DEFS.SOURCE}`),
        valueOptions: riskSourceOptions,
        sortable: false,
      },
      {
        ...riskScoreColumnType,
        field: VULNERABILITY_COLUMN_DEFS.SCORE,
        headerName: tRisks(`tableHeaders.${VULNERABILITY_COLUMN_DEFS.SCORE}`),
      },
      {
        ...riskSeverityColumnType,
        field: VULNERABILITY_COLUMN_DEFS.SEVERITY,
        headerName: tRisks(
          `tableHeaders.${VULNERABILITY_COLUMN_DEFS.SEVERITY}`,
        ),
        sortable: false,
      },
      {
        ...riskScoreColumnType,
        field: VULNERABILITY_COLUMN_DEFS.CVSSV3,
        headerName: tRisks(`tableHeaders.${VULNERABILITY_COLUMN_DEFS.CVSSV3}`),
        valueGetter: (_value, row: Vulnerability) =>
          row.cve?.primary.cvss.v3?.score,
      },
      {
        ...datetimeColumnType,
        field: VULNERABILITY_COLUMN_DEFS.FIRST_DETECTED_TIME,
        headerName: tRisks(
          `tableHeaders.${VULNERABILITY_COLUMN_DEFS.FIRST_DETECTED_TIME}`,
        ),
      },
      {
        ...datetimeColumnType,
        field: VULNERABILITY_COLUMN_DEFS.LAST_DETECTED_TIME,
        headerName: tRisks(
          `tableHeaders.${VULNERABILITY_COLUMN_DEFS.LAST_DETECTED_TIME}`,
        ),
      },
      {
        ...datetimeColumnType,
        field: VULNERABILITY_COLUMN_DEFS.ISSUE_PUBLISH_TIME,
        headerName: tRisks(
          `tableHeaders.${VULNERABILITY_COLUMN_DEFS.ISSUE_PUBLISH_TIME}`,
        ),
      },
      {
        ...datetimeColumnType,
        field: VULNERABILITY_COLUMN_DEFS.ISSUE_UPDATE_TIME,
        headerName: tRisks(
          `tableHeaders.${VULNERABILITY_COLUMN_DEFS.ISSUE_UPDATE_TIME}`,
        ),
      },
      {
        ...smallStringColumnType,
        field: VULNERABILITY_COLUMN_DEFS.CVE,
        headerName: tRisks(`tableHeaders.${VULNERABILITY_COLUMN_DEFS.CVE}`),
        minWidth: 150,
        renderCell: (params) => {
          return <CVECell row={params.row} />;
        },
        sortable: false,
        filterable: false,
        resizable: false,
      },
      {
        field: VULNERABILITY_COLUMN_DEFS.ACTIONS,
        headerName: tRisks(`tableHeaders.${VULNERABILITY_COLUMN_DEFS.ACTIONS}`),
        type: 'actions',
        width: 180,
        minWidth: 180,
        renderCell: (_params) => {
          return <CustomChip variant="unassigned" label="Coming Soon..." />;
        },
        sortable: false,
        filterable: false,
        resizable: false,
      },
      {
        ...hiddenColumnType,
        field: VULNERABILITY_COLUMN_DEFS.ISSUE_ID,
        headerName: tRisks(
          `tableHeaders.${VULNERABILITY_COLUMN_DEFS.ISSUE_ID}`,
        ),
        filterOperators: getFilteredGridStringOperators([
          GridStringOperator.Equals,
        ]),
      },
    ],
    [tRisks],
  );
};
